import { useEffect, useMemo, useState } from 'react';
import type { BillingInput, OrderDto, OrderEditPreviewDto } from '@ecom/contracts';
import { useOrdersStore } from '@/stores/orders.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { money } from '@/lib/format';
import { OrderLinesEditor, type EditableLine, type PricedLine } from '@/components/order-lines-editor';
import { Choices } from '@/components/fields';
import { AmountRow, Sheet } from '@/components/page';
import { Alert, Button, Field, Input, Textarea } from '@/components/ui';
import { BillingFields } from './new';

/**
 * Changing an order staff already have (ADR-062).
 *
 * Two sheets, because they are two kinds of change: the details — who to
 * reach, the note, who is billed — never touch the money; the items do, and so
 * are allowed only on a cash order nobody has shipped, paid or invoiced, priced
 * by the server before they are saved. Where it goes is corrected on the
 * address card, which keeps the city that priced the delivery (ADR-059).
 */

/** Statuses where the items can still change (the API is the judge; this only hides what it would refuse). */
const ITEMS_EDITABLE = ['AWAITING_COLLECTION', 'PROCESSING', 'READY_FOR_PICKUP'];

/** Whether the items can change — a cash order not yet shipped, with every line still on sale. */
export function itemsEditable(order: OrderDto): boolean {
  return (
    ITEMS_EDITABLE.includes(order.status) &&
    order.payment?.provider === 'cod' &&
    order.payment.status === 'PENDING' &&
    order.items.every((item) => item.variantId)
  );
}

export function detailsEditable(order: OrderDto): boolean {
  return order.status !== 'CANCELLED' && order.status !== 'REFUNDED';
}

function billingOf(order: OrderDto): BillingInput | null {
  const billing = order.billing;
  if (!billing) return null;
  return {
    name: billing.name,
    ...(billing.companyName ? { companyName: billing.companyName } : {}),
    ...(billing.line1 ? { line1: billing.line1 } : {}),
    ...(billing.city ? { city: billing.city } : {}),
    ...(billing.vatNumber ? { vatNumber: billing.vatNumber } : {}),
  };
}

export function EditDetailsSheet({ order, open, onClose }: { order: OrderDto; open: boolean; onClose: () => void }) {
  const { updateDetails, updating, currentError } = useOrdersStore();
  const t = useT();
  const [email, setEmail] = useState(order.email);
  const [phone, setPhone] = useState(order.phone ?? '');
  const [note, setNote] = useState(order.customerNote ?? '');
  const [billing, setBilling] = useState<BillingInput | null>(() => billingOf(order));
  const [reason, setReason] = useState('');

  // Every opening starts from the order as it is now.
  useEffect(() => {
    if (!open) return;
    setEmail(order.email);
    setPhone(order.phone ?? '');
    setNote(order.customerNote ?? '');
    setBilling(billingOf(order));
    setReason('');
  }, [open, order]);

  const billingChanged = JSON.stringify(billing) !== JSON.stringify(billingOf(order));

  async function submit() {
    const ok = await updateDetails(order.id, {
      ...(email.trim() !== order.email ? { email: email.trim() } : {}),
      ...(phone.trim() !== (order.phone ?? '') ? { phone: phone.trim() } : {}),
      ...(note.trim() !== (order.customerNote ?? '') ? { note: note.trim() || null } : {}),
      ...(billingChanged ? { billing: billing?.name.trim() ? billing : null } : {}),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    });
    if (ok) onClose();
  }

  return (
    <Sheet
      open={open}
      title={t('editDetailsTitle')}
      subtitle={t('editDetailsBody')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={updating}>
            {t('cancel')}
          </Button>
          <Button type="submit" form="order-details" disabled={updating}>
            {updating ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <form
        id="order-details"
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {/* Where it goes is corrected on the address card, which keeps the city that priced it (ADR-059). */}
        <p className="rounded-control bg-surface-sunken px-3 py-2 text-sm text-ink-muted">{t('editDetailsAddressElsewhere')}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('email')} htmlFor="edit-email">
            <Input
              id="edit-email"
              type="email"
              dir="ltr"
              maxLength={254}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <Field label={t('contactMobile')} htmlFor="edit-phone">
            <Input
              id="edit-phone"
              dir="ltr"
              inputMode="tel"
              maxLength={30}
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          </Field>
        </div>

        <Field label={t('customerNote')} htmlFor="edit-note" hint={t('customerNoteHint')}>
          <Textarea id="edit-note" rows={2} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>

        {billing ? (
          <BillingFields value={billing} onChange={setBilling} onRemove={() => setBilling(null)} />
        ) : (
          <div>
            <Button variant="secondary" size="sm" onClick={() => setBilling({ name: '' })}>
              {t('billABusiness')}
            </Button>
          </div>
        )}

        <Field label={t('editReason')} htmlFor="edit-reason" hint={t('reasonHint')}>
          <Textarea id="edit-reason" rows={2} maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>

        {currentError ? <Alert>{currentError}</Alert> : null}
      </form>
    </Sheet>
  );
}

function linesOf(order: OrderDto): EditableLine[] {
  return order.items.map((item) => ({
    key: item.id,
    itemId: item.id,
    variantId: item.variantId ?? '',
    label: item.name,
    sku: item.sku,
    detail: Object.entries(item.optionLabels)
      .map(([name, value]) => `${name}: ${value}`)
      .join(', '),
    quantity: item.quantity,
    imageUrl: item.imageUrl,
  }));
}

export function EditItemsSheet({ order, open, onClose }: { order: OrderDto; open: boolean; onClose: () => void }) {
  const { previewItems, updateItems, updating, currentError } = useOrdersStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [lines, setLines] = useState<EditableLine[]>(() => linesOf(order));
  const [shippingMethodId, setShippingMethodId] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<OrderEditPreviewDto | null>(null);
  const [pricing, setPricing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLines(linesOf(order));
    setShippingMethodId('');
    setReason('');
    setPreview(null);
  }, [open, order]);

  const request = useMemo(
    () => ({
      items: lines.map((line) => ({
        ...(line.itemId ? { itemId: line.itemId } : {}),
        variantId: line.variantId,
        quantity: line.quantity,
      })),
      ...(shippingMethodId ? { shippingMethodId } : {}),
    }),
    [lines, shippingMethodId],
  );

  // The server prices every change a moment after typing stops; nothing is computed here.
  useEffect(() => {
    if (!open || lines.length === 0) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPricing(true);
    const timer = setTimeout(() => {
      void previewItems(order.id, request).then((answer) => {
        if (cancelled) return;
        setPreview(answer);
        setPricing(false);
      });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, order.id, request, lines.length, previewItems]);

  const currency = order.totals.currency;
  const fmt = (halalas: number) => money(halalas, locale, currency);
  const priced = useMemo(() => {
    const map = new Map<string, PricedLine>();
    for (const line of lines) {
      const answer = preview?.items.find((item) => (line.itemId ? item.itemId === line.itemId : !item.itemId && item.variantId === line.variantId));
      if (answer) map.set(line.key, { unitPrice: answer.unitPrice, lineTotal: answer.lineTotal });
    }
    return map;
  }, [preview, lines]);

  const ready = !updating && !pricing && preview !== null && lines.length > 0;

  async function submit() {
    if (!ready) return;
    const ok = await updateItems(order.id, { ...request, ...(reason.trim() ? { reason: reason.trim() } : {}) });
    if (ok) onClose();
  }

  return (
    <Sheet
      open={open}
      title={t('editItemsTitle')}
      subtitle={t('editItemsBody')}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={updating}>
            {t('cancel')}
          </Button>
          <Button type="submit" form="order-items" disabled={!ready}>
            {updating ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <form
        id="order-items"
        className="flex flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <OrderLinesEditor lines={lines} priced={priced} onChange={setLines} currency={currency} disabled={updating} />

        {preview && preview.shippingOptions.length > 0 ? (
          <Choices
            legend={t('deliveryMethod')}
            name="edit-delivery"
            options={preview.shippingOptions.map((option) => ({
              value: option.methodId,
              label: option.name,
              hint: option.isPickup ? t('pickupAtStore') : undefined,
              aside: <span data-numeric>{option.price === 0 ? t('free') : fmt(option.price)}</span>,
            }))}
            value={shippingMethodId || preview.selectedShippingMethodId || null}
            onChange={setShippingMethodId}
          />
        ) : null}

        {preview ? (
          // The order page's totals, as they would read after the change.
          <dl className="flex flex-col gap-2 rounded-control border border-line p-4 text-sm">
            <AmountRow label={t('subtotal')} value={fmt(preview.totals.subtotal)} />
            {preview.totals.promotionTotal > 0 ? (
              <AmountRow label={t('promotionSavings')} value={`−${fmt(preview.totals.promotionTotal)}`} />
            ) : null}
            {preview.totals.discountTotal > 0 ? (
              <AmountRow
                label={order.discountCode ? `${t('discount')} (${order.discountCode})` : t('discount')}
                value={`−${fmt(preview.totals.discountTotal)}`}
              />
            ) : null}
            <AmountRow label={t('shipping')} value={fmt(preview.totals.shippingTotal)} />
            {preview.totals.paymentFee > 0 ? <AmountRow label={t('paymentFee')} value={fmt(preview.totals.paymentFee)} /> : null}
            <AmountRow label={t('newTotal')} value={fmt(preview.totals.grandTotal)} emphasis />
            <p className="text-xs text-ink-muted">{t('vatIncluded', { amount: fmt(preview.totals.taxTotal) })}</p>
          </dl>
        ) : null}

        {preview && preview.difference !== 0 ? (
          // What changes at the door: the courier collects the new total.
          <p className="rounded-control bg-surface-sunken px-3 py-2 text-sm text-ink">
            {preview.difference > 0
              ? t('collectMore', { amount: fmt(preview.difference) })
              : t('collectLess', { amount: fmt(-preview.difference) })}
          </p>
        ) : null}

        <Field label={t('editReason')} htmlFor="edit-items-reason" hint={t('reasonHint')}>
          <Textarea
            id="edit-items-reason"
            rows={2}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>

        {currentError ? <Alert>{currentError}</Alert> : null}
      </form>
    </Sheet>
  );
}
